import React, { useRef, useState, useCallback } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import PropTypes from 'prop-types';
import { TableHeader, TableHeaderContainer, ResizeHandle } from '../layout';
import { SortIcons } from './sortIcons';
import { FaGripLinesVertical } from 'react-icons/fa';
import { ACTION_COLUMNS } from '../hooks/useColumnOrder';

const ItemTypes = {
  COLUMN: 'column'
};

/**
 * @typedef {Object} Column
 * @property {string} dataField
 * @property {string} text
 * @property {Object} [headerStyle]
 */

/**
 * @typedef {Object} SortConfig
 * @property {string} key
 * @property {string} direction
 */

/**
 * @param {Object} props
 * @param {React.ReactNode} props.children
 * @param {Column} props.column
 * @param {number} props.index
 * @param {Function} props.moveColumn
 * @param {Function} props.requestSort
 * @param {SortConfig} props.sortConfig
 * @param {Function} props.onResize
 * @param {number[]} props.columnWidths
 * @param {Column[]} props.columnOrder
 */
export const DraggableTh = ({
  children,
  column,
  index,
  moveColumn,
  requestSort,
  sortConfig,
  onResize,
  columnWidths,
  columnOrder
}) => {
  const dragRef = useRef(null);
  const resizeRef = useRef(null);
  const [resizing, setResizing] = useState(false);
  const [startX, setStartX] = useState(0);
  const [startWidth, setStartWidth] = useState(0);
  const [currentWidth, setCurrentWidth] = useState(columnWidths[index]);
  const [animationFrameId, setAnimationFrameId] = useState(null);

  const isActionColumn = ACTION_COLUMNS.includes(column.dataField);

  const [{ isDragging }, drag] = useDrag({
    type: ItemTypes.COLUMN,
    item: { index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging()
    }),
    canDrag: () => !isActionColumn
  });

  const [, drop] = useDrop({
    accept: ItemTypes.COLUMN,
    hover(item) {
      if (!dragRef.current) return;

      const dragIndex = item.index;
      const hoverIndex = index;
      if (
        dragIndex === hoverIndex ||
        ACTION_COLUMNS.includes(columnOrder[dragIndex].dataField)
      )
        return;
      moveColumn(dragIndex, hoverIndex);

      item.index = hoverIndex;
    }
  });

  const handleMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setResizing(true);
    setStartX(e.pageX);
    setStartWidth(currentWidth);
  };

  const handleMouseMove = (e) => {
    if (resizing) {
      if (animationFrameId) cancelAnimationFrame(animationFrameId);

      const frameId = requestAnimationFrame(() => {
        const minWidth = resizeRef.current.firstChild.offsetWidth;
        const newWidth = startWidth + (e.pageX - startX);
        const width = Math.max(newWidth, minWidth + 25);

        setCurrentWidth(width);
      });

      setAnimationFrameId(frameId);
    }
  };

  const handleMouseUp = useCallback(() => {
    setResizing(false);
    if (animationFrameId) cancelAnimationFrame(animationFrameId);
  }, [animationFrameId, currentWidth, index, onResize]);

  React.useEffect(() => {
    if (resizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [resizing, handleMouseMove, handleMouseUp, animationFrameId]);

  drag(dragRef);
  React.useEffect(() => {
    const handleClick = (e) => {
      if (resizing) {
        e.stopPropagation();
      }
    };

    const refCurrent = resizeRef.current;
    onResize(index, currentWidth);
    refCurrent.addEventListener('click', handleClick);

    return () => {
      refCurrent.removeEventListener('click', handleClick);
    };
  }, [resizing]);
  drop(resizeRef);

  return (
    <TableHeader
      ref={resizeRef}
      style={{
        ...column.headerStyle,
        opacity: isDragging ? 0.5 : 1,
        cursor: !isActionColumn ? 'pointer' : 'auto',
        width: currentWidth,
        position: 'relative'
      }}
      onClick={() => !isActionColumn && requestSort(column.dataField)}
    >
      <TableHeaderContainer
        ref={dragRef}
        resizing={resizing}
        style={{ whiteSpace: 'nowrap', display: 'inline-flex', justifyContent: 'center' }}
      >
        {children}
        {!isActionColumn && (
          <SortIcons
            direction={
              sortConfig?.key === column.dataField ? sortConfig.direction : null
            }
          />
        )}
      </TableHeaderContainer>
      {!isActionColumn && (
        <ResizeHandle onMouseDown={handleMouseDown}>
          <FaGripLinesVertical color="#d2d8df" />
        </ResizeHandle>
      )}
    </TableHeader>
  );
};

DraggableTh.propTypes = {
  children: PropTypes.node.isRequired,
  column: PropTypes.shape({
    dataField: PropTypes.string.isRequired,
    text: PropTypes.string.isRequired,
    headerStyle: PropTypes.object
  }).isRequired,
  index: PropTypes.number.isRequired,
  moveColumn: PropTypes.func.isRequired,
  requestSort: PropTypes.func.isRequired,
  sortConfig: PropTypes.shape({
    key: PropTypes.string,
    direction: PropTypes.string
  }).isRequired,
  onResize: PropTypes.func.isRequired,
  columnWidths: PropTypes.arrayOf(PropTypes.number).isRequired,
  columnOrder: PropTypes.arrayOf(PropTypes.object).isRequired
};
